/**
 * AgentBuilderPage
 *
 * Multi-step form for creating a new AI agent:
 *   Step 1 — Name, personality / system prompt
 *   Step 2 — Voice selection (VAPI voice IDs) & integration toggles
 *   Step 3 — Review & Create
 *
 * On submit, calls POST /api/agents and then redirects to the detail page.
 */

import React, { useState } from 'react';
import {
  CCard,
  CCardBody,
  CCardHeader,
  CCardFooter,
  CCol,
  CRow,
  CButton,
  CFormInput,
  CFormTextarea,
  CFormSelect,
  CFormSwitch,
  CFormLabel,
  CSpinner,
  CAlert,
  CContainer,
  CProgress,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilArrowLeft, cilArrowRight, cilCheckCircle } from '@coreui/icons';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import config from '../../../utils/EnvironmentSwitcher';

// Available VAPI voice options (extend as needed)
const VOICE_OPTIONS = [
  { label: 'Default (no voice)', value: '' },
  { label: 'Alloy', value: 'alloy' },
  { label: 'Echo', value: 'echo' },
  { label: 'Fable', value: 'fable' },
  { label: 'Onyx', value: 'onyx' },
  { label: 'Nova', value: 'nova' },
  { label: 'Shimmer', value: 'shimmer' },
];

const STEPS = ['Basic Info', 'Voice & Integrations', 'Review'];

const AgentBuilderPage = () => {
  const navigate = useNavigate();

  // Form state
  const [name, setName] = useState('');
  const [personality, setPersonality] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [voiceId, setVoiceId] = useState('');
  const [integrations, setIntegrations] = useState({
    calendar: false,
    phone: false,
    crm: false,
  });

  // Wizard state
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  /* ---- Helpers ---------------------------------------------------------- */

  const toggleIntegration = (key) => {
    setIntegrations((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const canAdvance = () => {
    if (step === 0) return name.trim().length > 0;
    return true; // steps 1 & 2 are optional / review
  };

  /* ---- Submit ----------------------------------------------------------- */

  const handleCreate = async () => {
    try {
      setSubmitting(true);
      setError(null);
      const auth = getAuth();
      const token = await auth.currentUser.getIdToken();

      const body = {
        name: name.trim(),
        personality: personality.trim(),
        systemPrompt: systemPrompt.trim(),
        voiceId: voiceId || null,
        integrations,
      };

      const res = await fetch(`${config.apiUrl}/api/agents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Create failed: ${res.status}`);
      }

      const data = await res.json();
      // Navigate to the newly created agent detail page
      navigate(`/dashboard/agents/${data.agent?.id || data.id}`);
    } catch (err) {
      console.error('AgentBuilderPage: create error', err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  /* ---- Step content ----------------------------------------------------- */

  const renderStep = () => {
    switch (step) {
      /* Step 0: Basic Info */
      case 0:
        return (
          <>
            <div className="mb-3">
              <CFormLabel style={{ color: 'var(--text-secondary)' }}>Agent Name *</CFormLabel>
              <CFormInput
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Customer Support Bot"
              />
            </div>
            <div className="mb-3">
              <CFormLabel style={{ color: 'var(--text-secondary)' }}>Personality / Description</CFormLabel>
              <CFormTextarea
                rows={3}
                value={personality}
                onChange={(e) => setPersonality(e.target.value)}
                placeholder="A friendly, professional assistant that handles customer queries…"
              />
            </div>
            <div className="mb-3">
              <CFormLabel style={{ color: 'var(--text-secondary)' }}>System Prompt</CFormLabel>
              <CFormTextarea
                rows={5}
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                placeholder="You are a helpful AI assistant for …"
              />
              <small style={{ color: 'var(--text-muted)' }}>
                This is the base instruction sent to the LLM for every conversation.
              </small>
            </div>
          </>
        );

      /* Step 1: Voice & Integrations */
      case 1:
        return (
          <>
            <div className="mb-4">
              <CFormLabel style={{ color: 'var(--text-secondary)' }}>Voice (VAPI)</CFormLabel>
              <CFormSelect value={voiceId} onChange={(e) => setVoiceId(e.target.value)}>
                {VOICE_OPTIONS.map((v) => (
                  <option key={v.value} value={v.value}>
                    {v.label}
                  </option>
                ))}
              </CFormSelect>
              <small style={{ color: 'var(--text-muted)' }}>
                Select a voice for phone/voice interactions. Leave empty for text-only.
              </small>
            </div>

            <h5 style={{ color: 'var(--text-primary)' }} className="mb-3">
              Integrations
            </h5>
            {Object.entries(integrations).map(([key, enabled]) => (
              <div key={key} className="d-flex justify-content-between align-items-center mb-3">
                <span style={{ color: 'var(--text-secondary)', textTransform: 'capitalize' }}>
                  {key}
                </span>
                <CFormSwitch
                  checked={enabled}
                  onChange={() => toggleIntegration(key)}
                  label={enabled ? 'On' : 'Off'}
                />
              </div>
            ))}
            <small style={{ color: 'var(--text-muted)' }}>
              Integration connections are configured in Phase 2.
            </small>
          </>
        );

      /* Step 2: Review */
      case 2:
        return (
          <>
            <h5 style={{ color: 'var(--text-primary)' }} className="mb-3">
              Review Your Agent
            </h5>
            <table className="table table-borderless" style={{ color: 'var(--text-secondary)' }}>
              <tbody>
                <tr>
                  <td style={{ fontWeight: 600, width: '35%' }}>Name</td>
                  <td>{name}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 600 }}>Personality</td>
                  <td>{personality || '—'}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 600 }}>System Prompt</td>
                  <td style={{ whiteSpace: 'pre-wrap', maxHeight: 120, overflow: 'auto' }}>
                    {systemPrompt || '—'}
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 600 }}>Voice</td>
                  <td>{voiceId || 'None'}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 600 }}>Integrations</td>
                  <td>
                    {Object.entries(integrations)
                      .filter(([, v]) => v)
                      .map(([k]) => k)
                      .join(', ') || 'None'}
                  </td>
                </tr>
              </tbody>
            </table>
          </>
        );

      default:
        return null;
    }
  };

  /* ---- Render ----------------------------------------------------------- */

  return (
    <CContainer className="py-4" style={{ maxWidth: 720 }}>
      <CButton
        color="link"
        className="mb-3 px-0"
        onClick={() => navigate('/dashboard/agents')}
        style={{ color: 'var(--electric-blue)' }}
      >
        <CIcon icon={cilArrowLeft} className="me-1" /> Back to Agents
      </CButton>

      <h2 className="text-gradient mb-4">Create New Agent</h2>

      {/* Progress bar */}
      <div className="mb-4">
        <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          {STEPS.map((label, i) => (
            <span key={label} style={{ fontWeight: i === step ? 700 : 400, color: i === step ? 'var(--electric-blue)' : undefined }}>
              {label}
            </span>
          ))}
        </div>
        <CProgress value={((step + 1) / STEPS.length) * 100} />
      </div>

      {error && <CAlert color="danger">{error}</CAlert>}

      <CCard>
        <CCardHeader style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
          Step {step + 1}: {STEPS[step]}
        </CCardHeader>
        <CCardBody>{renderStep()}</CCardBody>
        <CCardFooter className="d-flex justify-content-between">
          <CButton
            color="secondary"
            variant="outline"
            disabled={step === 0}
            onClick={() => setStep((s) => s - 1)}
          >
            <CIcon icon={cilArrowLeft} className="me-1" /> Back
          </CButton>

          {step < STEPS.length - 1 ? (
            <CButton color="primary" disabled={!canAdvance()} onClick={() => setStep((s) => s + 1)}>
              Next <CIcon icon={cilArrowRight} className="ms-1" />
            </CButton>
          ) : (
            <CButton color="primary" disabled={submitting || !name.trim()} onClick={handleCreate}>
              {submitting ? (
                <CSpinner size="sm" className="me-2" />
              ) : (
                <CIcon icon={cilCheckCircle} className="me-2" />
              )}
              Create Agent
            </CButton>
          )}
        </CCardFooter>
      </CCard>
    </CContainer>
  );
};

export default AgentBuilderPage;


